// Feature-flag + entity data layer (Task 24; plan L451-453; spec §5.11/§6.8;
// migrations 20260926000008 `feature_flags` / 20260926000009 `entity`).
//
// `hostProducts` is the only flag the portal reads client-side — it's the
// one row seeded `client_visible = true` (RLS: `authenticated` sees
// client_visible rows only, 20260926000008). `launchFeeWaiver` and
// `aiKillSwitch` are server-only by design and deliberately NOT queried
// here.
//
// entity.namibia_ltd gates the host-product UI TOGETHER with the flag
// (20260926000009's own header comment: "The portal renders that UI only
// when entity.namibia_ltd AND flags.hostProducts") — both must be true
// before Business Pro / Featured / Verified badge / channel sync render
// their "coming later" state instead of a bare "not available yet".
import { useQuery } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';

export function useHostProductsGate(supabase: SupabaseClient) {
  const flagQuery = useQuery({
    queryKey: ['feature-flag', 'hostProducts'],
    queryFn: async (): Promise<boolean> => {
      const { data, error } = await supabase
        .from('feature_flags')
        .select('enabled')
        .eq('key', 'hostProducts')
        .maybeSingle();
      if (error) throw new Error(error.message);
      return !!(data as { enabled?: boolean } | null)?.enabled;
    },
  });

  const entityQuery = useQuery({
    queryKey: ['entity', 'namibia_ltd'],
    queryFn: async (): Promise<boolean> => {
      const { data, error } = await supabase.from('entity').select('namibia_ltd').eq('singleton', true).maybeSingle();
      if (error) throw new Error(error.message);
      return !!(data as { namibia_ltd?: boolean } | null)?.namibia_ltd;
    },
  });

  return {
    /** True only once BOTH the flag and the entity are on — the exact
     * ruling recorded in 20260926000009's header comment. */
    gateOpen: !!flagQuery.data && !!entityQuery.data,
    loading: flagQuery.isLoading || entityQuery.isLoading,
  };
}
